import { BusinessObjectBase } from './base';
import { slotSchema } from '@schemas/business-objects/slot';
import type { ActivityAssignment } from './activity-assignment';
import type { AlternateStudentAvailability } from './alternate-student-availability';
import type { StudentAssignment } from './student-assignment';
import type { TermTime } from './term-time';
import type { DayOfWeek, Id } from '../base-types';

export interface SlotData {
  readonly id: Id;
  readonly dayOfWeek?: DayOfWeek;
  readonly timeId: Id;
  readonly time?: TermTime;
  readonly activityAssignments?: ActivityAssignment[];
  readonly studentAssignments?: StudentAssignment[];
  readonly alternateStudentAvailabilities?: AlternateStudentAvailability[];
}

export interface Slot extends Readonly<SlotData> {}
export class Slot extends BusinessObjectBase {
  static readonly entityName = 'Slot' as const;
  static readonly plural = 'slots';
  static schema = slotSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Slot[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Slot(data));
  }

  static async findOne(filter?: any): Promise<Slot | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Slot(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Slot> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Slot(data);
  }

  static async create(data: Omit<SlotData, 'id'>): Promise<Slot> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Slot(result);
  }

  static async createMany(data: Omit<SlotData, 'id'>[]): Promise<Slot[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Slot(item));
  }

  static async updateById(id: Id, updates: Partial<SlotData>): Promise<Slot> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Slot(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
